import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  alpha,
  CircularProgress,
  Divider,
  Grid,
  InputBase,
  Card,
  CardContent,
  useTheme,
  Fade,
  TablePagination,
  Stack,
  LinearProgress
} from '@mui/material';
import {
  PenSquare,
  Blocks,
  LayoutTemplate,
  Edit2,
  Trash2,
  Copy,
  Star,
  StarOff,
  Sparkles,
  ChevronRight,
  Search,
  Plus,
  ArrowDown,
  ArrowUp,
  TestTubeDiagonal,
  Filter,
  Calendar,
  RefreshCw
} from 'lucide-react';
import PageHeader from './PageHeader';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiUtils';

const CreatePromptCards = [
  {
    title: "Custom Editor",
    description: "Start with a blank canvas and write from scratch. Perfect for experienced prompt engineers.",
    icon: <PenSquare size={24} />,
    path: '/prompts/editor',
    color: '#E04C16',
    gradient: 'linear-gradient(135deg, #FF8C42, #E04C16)'
  },
  {
    title: "Block Builder",
    description: "Build prompts using our intuitive drag-and-drop interface. Great for visualizing your prompt structure.",
    icon: <Blocks size={24} />,
    path: '/prompts/builder',
    color: '#4158D0',
    gradient: 'linear-gradient(135deg, #6A7BFF, #4158D0)'
  },
  {
    title: "Template Library",
    description: "Start from our pre-made templates. The fastest way to create effective prompts for common use cases.",
    icon: <LayoutTemplate size={24} />,
    path: '/prompts/templates',
    color: '#43A047',
    gradient: 'linear-gradient(135deg, #66BB6A, #43A047)'
  }
];

const PromptLab = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState({ field: 'created_at', direction: 'desc' });
  const { authenticatedRequest } = useAuth();

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    if (prompts.length > 0) {
      // Filter prompts based on search query
      let filtered = prompts;
      
      if (searchQuery) {
        filtered = prompts.filter(prompt => 
          prompt.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (prompt.description && prompt.description.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      }
      
      // Sort filtered prompts
      filtered = [...filtered].sort((a, b) => {
        const fieldA = a[sort.field];
        const fieldB = b[sort.field];
        
        if (sort.field === 'created_at' || sort.field === 'updated_at') {
          return sort.direction === 'asc' 
            ? new Date(fieldA) - new Date(fieldB)
            : new Date(fieldB) - new Date(fieldA);
        }
        
        if (typeof fieldA === 'string' && typeof fieldB === 'string') {
          return sort.direction === 'asc'
            ? fieldA.localeCompare(fieldB)
            : fieldB.localeCompare(fieldA);
        }
        
        return sort.direction === 'asc' ? fieldA - fieldB : fieldB - fieldA;
      });
      
      setFilteredPrompts(filtered);
    }
  }, [prompts, searchQuery, sort]);

  const fetchPrompts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await authenticatedRequest(() => apiClient.getSavedPrompts());
      setPrompts(data.user_prompts || []);
    } catch (err) {
      setError('Failed to fetch prompts');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshPrompts = async () => {
    if (isRefreshing) return;
    
    try {
      setIsRefreshing(true);
      const data = await authenticatedRequest(() => apiClient.getSavedPrompts());
      setPrompts(data.user_prompts || []);
      setNotification({
        open: true,
        message: 'Prompts refreshed successfully',
        severity: 'success'
      });
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to refresh prompts',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleToggleFavorite = async (promptId) => {
    try {
      const result = await authenticatedRequest(() => apiClient.togglePromptFavorite(promptId));
      setPrompts(prompts.map(p => 
        p.id === promptId ? { ...p, is_favorite: result.is_favorite } : p
      ));
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to update favorite status',
        severity: 'error'
      });
    }
  };

  const handleDuplicate = async (promptId) => {
    try {
      const newPrompt = await authenticatedRequest(() => apiClient.duplicatePrompt(promptId));
      setPrompts([...prompts, newPrompt]);
      setNotification({
        open: true,
        message: 'Prompt duplicated successfully',
        severity: 'success'
      });
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to duplicate prompt',
        severity: 'error'
      });
    }
  };

  const handleDelete = async () => {
    try {
      await authenticatedRequest(() => apiClient.deletePrompt(promptToDelete));
      setPrompts(prompts.filter(prompt => prompt.id !== promptToDelete));
      setNotification({
        open: true,
        message: 'Prompt deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      setNotification({
        open: true,
        message: 'Failed to delete prompt',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setPromptToDelete(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    setSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sort.field !== field) return null;
    return sort.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Filter the displayed prompts by pagination
  const displayedPrompts = filteredPrompts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 4 } }}>
      <PageHeader
        category="Prompt Lab"
        title="Prompt Management"
        icon={
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #E04C16 0%, #8B5CF6 50%, #3B82F6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 10px rgba(224, 76, 22, 0.2)',
          }}>
            <TestTubeDiagonal size={24} color="#FFFFFF" strokeWidth={1.5} />
          </Box>
        }
        description="Create and manage your AI prompts with our advanced tools"
      />

      {/* Create New Prompt Section */}
      <Card 
        elevation={0} 
        sx={{ 
          mt: 4, 
          borderRadius: '16px', 
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.1),
          background: 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,247,250,0.9) 100%)',
          backdropFilter: 'blur(10px)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: '#1A2034',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Plus size={20} style={{ color: theme.palette.primary.main }} />
              Create New Prompt
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose your preferred method to create an AI prompt
            </Typography>
          </Box>

          <Grid container spacing={0}>
            {CreatePromptCards.map((card, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  onClick={() => navigate(card.path)}
                  sx={{
                    height: '100%',
                    p: { xs: 2, md: 2.5 },
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRight: index < CreatePromptCards.length - 1 ? 
                      { xs: 'none', md: '1px solid' } : 'none',
                    borderBottom: { 
                      xs: index < CreatePromptCards.length - 1 ? '1px solid' : 'none', 
                      md: 'none' 
                    },
                    borderColor: 'rgba(208, 213, 221, 0.6) !important',
                    transition: 'all 0.25s ease-in-out',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      backgroundColor: alpha(card.color, 0.05),
                      '& .icon-box': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 6px 12px ${alpha(card.color, 0.2)}`,
                      },
                      '& .title': {
                        color: card.color,
                      },
                      '& .arrow': {
                        transform: 'translateX(4px)',
                        opacity: 1,
                        color: card.color
                      },
                      '&::after': {
                        opacity: 1,
                        transform: 'translateY(0)'
                      }
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: card.gradient,
                      opacity: 0,
                      transform: 'translateY(3px)',
                      transition: 'all 0.25s ease-in-out'
                    }
                  }}
                >
                  <Box 
                    className="icon-box"
                    sx={{ 
                      width: 48,
                      height: 48,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1.5,
                      background: card.gradient,
                      color: 'white',
                      boxShadow: `0 4px 8px ${alpha(card.color, 0.2)}`,
                      transition: 'all 0.25s ease'
                    }}
                  >
                    {React.cloneElement(card.icon, { size: 24 })}
                  </Box>
                  <Typography 
                    variant="h6" 
                    className="title"
                    sx={{ 
                      fontWeight: 600, 
                      color: '#1A2034',
                      mb: 0.75,
                      fontSize: '1rem',
                      transition: 'color 0.25s ease'
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      flex: 1,
                      mb: 1.5,
                      fontSize: '0.8125rem',
                      lineHeight: 1.4
                    }}
                  >
                    {card.description}
                  </Typography>
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      mt: 'auto',
                      alignSelf: 'flex-start',
                      py: 0.5,
                      px: 1.25,
                      borderRadius: '6px',
                      backgroundColor: alpha(card.color, 0.08),
                      color: card.color,
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    Get Started
                    <ChevronRight 
                      size={14} 
                      className="arrow" 
                      style={{ 
                        marginLeft: 4,
                        opacity: 0.7,
                        transition: 'all 0.25s ease'
                      }} 
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Saved Prompts Section */}
      <Card 
        elevation={0} 
        sx={{ 
          mt: 4, 
          borderRadius: '16px', 
          border: '1px solid',
          borderColor: alpha(theme.palette.primary.main, 0.1),
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Header with controls */}
          <Box sx={{ 
            p: 3, 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1A2034',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Sparkles size={20} style={{ color: '#8B5CF6' }} />
                Saved Prompts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {filteredPrompts.length} {filteredPrompts.length === 1 ? 'prompt' : 'prompts'} available
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.75,
                  maxWidth: 250
                }}
              >
                <Search size={18} color={alpha('#000', 0.4)} />
                <InputBase
                  placeholder="Search prompts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ ml: 1, flex: 1, fontSize: '0.875rem' }}
                />
              </Box>
              <Tooltip title="Refresh Prompts">
                <IconButton 
                  size="small" 
                  onClick={refreshPrompts}
                  disabled={isRefreshing}
                  sx={{ 
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                  }}
                >
                  {isRefreshing ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshCw size={18} />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>

          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress sx={{ height: 3 }} />
            </Box>
          )}

          {/* Table of prompts */}
          <TableContainer>
            <Table sx={{ minWidth: 700 }}>
              <TableHead sx={{ backgroundColor: alpha('#f5f7fa', 0.6) }}>
                <TableRow>
                  <TableCell 
                    onClick={() => handleSort('name')}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: alpha('#000', 0.03) }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Name {getSortIcon('name')}
                    </Box>
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell
                    onClick={() => handleSort('created_at')}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: alpha('#000', 0.03) }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Created {getSortIcon('created_at')}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('updated_at')}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: alpha('#000', 0.03) }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Modified {getSortIcon('updated_at')}
                    </Box>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && !isRefreshing ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={30} />
                      <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading your prompts...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedPrompts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      {searchQuery ? (
                        <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
                          <Search size={32} style={{ color: alpha('#000', 0.2), margin: '0 auto 16px' }} />
                          <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
                            No matching prompts
                          </Typography>
                          <Typography color="text.secondary">
                            We couldn't find any prompts matching "{searchQuery}". Try a different search term.
                          </Typography>
                          <Button 
                            variant="outlined" 
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={() => setSearchQuery('')}
                          >
                            Clear Search
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center' }}>
                          <Sparkles size={32} style={{ color: alpha('#000', 0.2), margin: '0 auto 16px' }} />
                          <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
                            No prompts yet
                          </Typography>
                          <Typography color="text.secondary">
                            Create your first prompt using one of the methods above.
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedPrompts.map((prompt) => (
                    <TableRow key={prompt.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {prompt.is_favorite ? (
                            <Star size={16} fill="#FFD700" color="#FFD700" />
                          ) : null}
                          <Typography sx={{ fontWeight: 500 }}>{prompt.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={prompt.type || "Custom"} 
                          size="small"
                          sx={{ 
                            backgroundColor: alpha('#8B5CF6', 0.1),
                            color: '#8B5CF6',
                            fontWeight: 500
                          }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Calendar size={14} style={{ color: alpha('#000', 0.3) }} />
                          {new Date(prompt.created_at).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {new Date(prompt.updated_at).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={prompt.status || "Active"} 
                          size="small"
                          color={
                            prompt.status === 'active' ? 'success' : 
                            prompt.status === 'draft' ? 'default' : 
                            'primary'
                          }
                          sx={{ 
                            textTransform: 'capitalize',
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={prompt.is_favorite ? "Remove from favorites" : "Add to favorites"}>
                            <IconButton 
                              size="small"
                              onClick={() => handleToggleFavorite(prompt.id)}
                              sx={{ 
                                color: prompt.is_favorite ? '#FFD700' : undefined,
                                '&:hover': {
                                  backgroundColor: alpha('#FFD700', 0.1)
                                }
                              }}
                            >
                              {prompt.is_favorite ? 
                                <Star size={16} fill="#FFD700" /> : 
                                <StarOff size={16} />
                              }
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Duplicate">
                            <IconButton 
                              size="small"
                              onClick={() => handleDuplicate(prompt.id)}
                              sx={{ 
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                                  color: theme.palette.info.main
                                }
                              }}
                            >
                              <Copy size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small"
                              onClick={() => navigate(`/prompts/edit/${prompt.id}`)}
                              sx={{ 
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main
                                }
                              }}
                            >
                              <Edit2 size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                setPromptToDelete(prompt.id);
                                setDeleteDialogOpen(true);
                              }}
                              sx={{ 
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                  color: theme.palette.error.main
                                }
                              }}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredPrompts.length > 0 && (
            <TablePagination
              component="div"
              count={filteredPrompts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 35px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          fontWeight: 600,
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Trash2 size={20} color={theme.palette.error.main} />
          Delete Prompt
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Are you sure you want to delete this prompt?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone, and the prompt will be permanently removed from your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDelete}
            sx={{ 
              borderRadius: '8px',
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
            }}
          >
            Delete Prompt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert 
          severity={notification.severity}
          onClose={() => setNotification({ ...notification, open: false })}
          sx={{ 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            alignItems: 'center'
          }}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PromptLab; 